import { InjectionToken, Provider } from '@angular/core';
import { RolesEnum } from '@seatix/shared/domain';
import { createNoopInjectionToken } from 'ngxtension/create-injection-token';

/**
 * Interface for gate validation bypass checkers.
 * Implementations determine if a user can bypass gate restrictions.
 */
export interface GateValidationBypassChecker {
  canBypass(): boolean;
}

/**
 * Injection token and helpers for GateValidationBypassChecker.
 * Uses ngxtension pattern for type-safe dependency injection.
 *
 * @example
 * // Inject in service
 * readonly #bypassChecker = inject(GATE_VALIDATION_BYPASS_CHECKER, { optional: true });
 *
 * // Provide custom implementation
 * provideGateValidationBypassChecker(CustomBypassChecker)
 */
export const [
  injectGateValidationBypassChecker,
  provideGateValidationBypassChecker,
  GATE_VALIDATION_BYPASS_CHECKER,
] = createNoopInjectionToken<GateValidationBypassChecker>(
  'GateValidationBypassChecker',
);

/**
 * Token for configuring which roles can bypass gate validation.
 * Default: ['owner', 'manager']
 */
export const BYPASS_ROLES = new InjectionToken<RolesEnum[]>('BYPASS_ROLES', {
  providedIn: 'root',
  factory: () => ['owner', 'manager'],
});

/**
 * Provides the default role-based gate validation bypass.
 *
 * @param roles - Array of roles that can bypass gate validation
 * @returns Provider array to add to app config
 *
 * @example
 * // In app.config.ts
 * providers: [
 *   provideGateValidationBypass(['owner', 'manager'])
 * ]
 *
 * @example
 * // Only owners can bypass
 * provideGateValidationBypass(['owner'])
 */
export function provideGateValidationBypass(
  roles: RolesEnum[] = ['owner', 'manager'],
): Provider[] {
  return [{ provide: BYPASS_ROLES, useValue: roles }];
}
